/**
 * ProductTour . iter75 (Item B)
 *
 * First-time-user guided tour that fires once per case, after the persona
 * questionnaire. Walks the student through the real product structure of
 * Ministry of Exchange: five products plus the honest Manifest→Roam
 * dependency (which most users will have already unlocked via the persona
 * questionnaire's 4th question in iter75 Item A).
 *
 * UX rules (matched to OnboardingWalkthrough + PersonaQuestionnaire):
 *   - Skippable at EVERY step. X, Esc, and backdrop click all close and
 *     persist product_tour_seen=true so the tour never auto-fires again.
 *   - No forced completion, no fake urgency, no dark patterns.
 *   - Fires once per case. If dismissed (any outcome), never re-prompts.
 *   - Users can re-open from the dashboard footer via the same "Review setup
 *     options" link the iter55 vendor walkthrough uses; both re-open on
 *     demand without resetting the seen flag.
 *   - Portaled to document.body so parent transforms can't break its
 *     fixed positioning.
 *   - Copy reuses the honest section framing from `sectionFraming()` so
 *     nothing is invented; only the "what does each product exist for"
 *     value proposition is added on top of the neutral framing.
 *
 * Steps (6 total):
 *   1. Welcome + product overview
 *   2. Compass . visa + arrival
 *   3. Manifest . cohort matching (mentions the opt-in dependency
 *      honestly for users who answered No or skipped the persona modal)
 *   4. Smart Packing . personalised list
 *   5. Roam . weekend trips (mentions Roam→Manifest handoff)
 *   6. Concierge . human support + navigation cheatsheet
 */
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import {
  X, Compass, Users, Package, Map, HeadphonesIcon, ArrowLeft, ArrowRight, Sparkles,
} from "lucide-react";

const STEPS = [
  {
    key: "welcome",
    Icon: Sparkles,
    title: "Welcome. Here's how Ministry of Exchange fits together.",
    lede:  "Five products, one honest system. This tour takes about a minute and you can skip anytime.",
    body: (
      <ul className="mt-4 space-y-2 text-[13px] text-ink/85 leading-snug">
        <li><b>Northstar:</b> your visa path, funds, appointment booking, and country checklist.</li>
        <li><b>Cohort:</b> your exchange peers. Matching, requests, and group chat.</li>
        <li><b>Assembly:</b> coordinate group trips, arrivals, roommate hunts, and social plans with your cohort.</li>
        <li><b>Smart Packing:</b> a personalised list based on your airline, accommodation, and country.</li>
        <li><b>Drift:</b> real weekend destinations within ~300 km of your host city.</li>
        <li><b>Concierge:</b> a human you can email when something's stuck.</li>
      </ul>
    ),
  },
  {
    key: "compass",
    Icon: Compass,
    title: "Northstar. Your visa and arrival path.",
    lede:  "The official {country} checklist, funds guidance, and appointment booking portal.",
    body: (
      <div className="mt-4 space-y-3 text-[13px] text-ink/85 leading-snug">
        <p>Everything Compass shows is sourced from the country's official immigration site, not blog posts. Fees carry an INR conversion so you know the real cost. Appointment wait times come from other students who reported.</p>
        <p className="text-[12px] text-muted">On your dashboard it lives in "Your visa and arrival path" and "Personalised task list, grouped by stage".</p>
      </div>
    ),
  },
  {
    key: "manifest",
    Icon: Users,
    title: "Cohort. Your exchange peers.",
    lede:  "Match with peers headed to the same country. Coordinate trips, ask questions.",
    dependency: true,
    body: (
      <div className="mt-4 space-y-3 text-[13px] text-ink/85 leading-snug">
        <p>Cohort matches you with real students from Indian B-schools going to your host university, city, or country. Send connection requests, share notes, chat 1:1 or with your cohort.</p>
        <p><b>Assembly</b> (group weekend trips, arrivals, roommate hunts, dinners) is Cohort's sibling product — a top-level nav item of its own. Cohort is where you find and talk to people; Assembly is where you coordinate what you're doing together.</p>
        <p className="text-[12px] text-muted"><b>Note:</b> Manifest needs your explicit opt-in. If you said "Not right now" earlier, you'll see a clear gate banner when you open Manifest or Roam. Turning it on takes ten seconds from the Matches tab.</p>
      </div>
    ),
  },
  {
    key: "packing",
    Icon: Package,
    title: "Smart Packing. What to bring.",
    lede:  "A personalised cabin, checked-luggage, and arrival shopping list.",
    body: (
      <div className="mt-4 space-y-3 text-[13px] text-ink/85 leading-snug">
        <p>Six quick questions about your airline, accommodation, and dietary needs. Generates a checklist that tracks essentials, restricted items to verify, and things to buy after you land.</p>
        <p className="text-[12px] text-muted">Add your own custom items at any time. Fields marked with a red asterisk are required to save the list.</p>
      </div>
    ),
  },
  {
    key: "roam",
    Icon: Map,
    title: "Drift. Weekend trips near your host city.",
    lede:  "3-5 curated destinations within ~300 km, with honest cost ranges.",
    body: (
      <div className="mt-4 space-y-3 text-[13px] text-ink/85 leading-snug">
        <p>Drift is the discovery layer. Each destination card shows real distance, transit options, and a cost range in EUR and INR. One tap starts a Trip Assembly in Cohort so your peers can join.</p>
        <p className="text-[12px] text-muted">Drift browsing is free for everyone. To create or join a Trip Assembly you need to be visible on Cohort (see previous step).</p>
      </div>
    ),
  },
  {
    key: "concierge",
    Icon: HeadphonesIcon,
    title: "Concierge. A human when you're stuck.",
    lede:  "One inbox, one person. No ticketing system, no auto-responder.",
    body: (
      <div className="mt-4 space-y-3 text-[13px] text-ink/85 leading-snug">
        <p>Rejected at the consulate. Landlord ghosted you. Bank refuses to transfer. Reach out and a real person picks it up. Usually a same-day response.</p>
        <div className="mt-4 rounded-xl bg-tint p-3 border border-ink/10">
          <div className="text-[10px] uppercase tracking-widest text-elec font-bold">Navigation cheatsheet</div>
          <ul className="mt-1 space-y-0.5 text-[12px] text-ink/80">
            <li><b>Dashboard.</b> your homepage. Tasks, visa card, checklist, country reference.</li>
            <li><b>Cohort.</b> Matches, Requests, Cohort thread, Assembly, Messages (five tabs).</li>
            <li><b>Drift.</b> weekend trip discovery.</li>
            <li><b>Smart Packing.</b> your personalised list.</li>
            <li><b>Contact.</b> reach the Concierge.</li>
          </ul>
        </div>
      </div>
    ),
  },
];

export default function ProductTour({ open, country, onClose }) {
  const [idx, setIdx]         = useState(0);
  const [saving, setSaving]   = useState(false);
  const total = STEPS.length;
  const step = STEPS[idx] || STEPS[0];
  const isFirst = idx === 0;
  const isLast  = idx === total - 1;

  useEffect(() => { if (open) setIdx(0); }, [open]);

  // Body scroll-lock while modal is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const markSeen = useCallback(async (outcome) => {
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/cases/me/product-tour-seen", { outcome });
    } catch (_) {
      // Silent-fail. Never trap the user in the modal on a network hiccup.
    } finally {
      setSaving(false);
    }
  }, [saving]);

  const finish = useCallback(async () => {
    await markSeen("completed");
    onClose?.({ outcome: "completed" });
  }, [markSeen, onClose]);

  const skip = useCallback(async () => {
    await markSeen("skipped");
    onClose?.({ outcome: "skipped" });
  }, [markSeen, onClose]);

  // Esc key . equal weight to Skip.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") skip(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, skip]);

  if (!open) return null;

  const goNext = () => (isLast ? finish() : setIdx(idx + 1));
  const goPrev = () => { if (!isFirst) setIdx(idx - 1); };
  const StepIcon = step.Icon || Sparkles;

  // Substitute the caller's country into the step's lede if the template
  // contains "{country}"; leaves other steps untouched.
  const lede = (step.lede || "").replace("{country}", country || "your host country");

  return createPortal(
    <div
      data-testid="product-tour"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 animate-[fadeIn_150ms_ease-out]"
      style={{ backgroundColor: "rgb(10, 10, 10)" }}
      onClick={skip}
    >
      <div
        className="w-full max-w-2xl bg-paper rounded-2xl shadow-2xl border border-ink/10 overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-tour-title"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-ink/10 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-full bg-elec/10 text-elec flex items-center justify-center shrink-0">
              <StepIcon size={18} strokeWidth={2}/>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">
                Step {idx + 1} of {total}
              </div>
              <h2 id="product-tour-title" className="mt-0.5 text-xl md:text-2xl font-bold tracking-tight text-ink leading-snug">
                {step.title}
              </h2>
              <p className="mt-1 text-[13px] text-muted leading-snug">
                {lede}
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="product-tour-close"
            onClick={skip}
            aria-label="Close tour"
            className="shrink-0 w-9 h-9 rounded-full hover:bg-ink/5 flex items-center justify-center text-ink/60 hover:text-ink"
          >
            <X size={18} strokeWidth={2}/>
          </button>
        </div>

        {/* Body */}
        <div
          className="flex-1 overflow-y-auto px-6 py-4"
          data-testid={`product-tour-step-${step.key}`}
        >
          {step.body}
        </div>

        {/* Progress dots */}
        <div className="px-6 pt-3 pb-2 flex justify-center gap-1.5" aria-hidden="true">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-elec" : "w-1.5 bg-ink/20"}`}
            />
          ))}
        </div>

        {/* Footer navigation. Skip and Next carry equal weight. */}
        <div className="px-6 py-4 border-t border-ink/10 flex items-center justify-between gap-3">
          <button
            type="button"
            data-testid="product-tour-skip"
            onClick={skip}
            disabled={saving}
            className="px-4 h-10 rounded-full text-muted hover:text-ink text-sm font-semibold disabled:opacity-50"
          >
            Skip
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                type="button"
                data-testid="product-tour-prev"
                onClick={goPrev}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 h-10 rounded-full border border-ink/20 text-ink text-sm font-semibold hover:border-ink disabled:opacity-50"
              >
                <ArrowLeft size={13}/> Back
              </button>
            )}
            <button
              type="button"
              data-testid={isLast ? "product-tour-finish" : "product-tour-next"}
              onClick={goNext}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-5 h-10 rounded-full bg-ink text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {isLast ? "Finish" : "Next"} <ArrowRight size={13}/>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
